import React, { useCallback, useMemo, useState } from "react";
import { StyleSheet, View, Pressable } from "react-native";
import { DotsThree, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Menu } from "react-native-paper";

import CustomText from "../../common/general/CustomText/CustomText";
import Pill from "../../common/Pill/Pill";

interface AnnouncementItemProps {
  announcement: {
    id: string;
    title: string;
    message: string;
    author: string;
    date: string;
    status: "published" | "scheduled" | "draft";
    targetAudience: string;
    targetDetails?: string;
    created?: string;
    lastModified?: string;
    expirationDate?: string;
  };
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onPublish?: (id: string) => void;
  onReschedule?: (id: string) => void;
}

const AnnouncementItem: React.FC<AnnouncementItemProps> = ({
  announcement,
  onEdit,
  onDelete,
  onPublish,
  onReschedule,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showTargetDetails, setShowTargetDetails] = useState(false);

  const getStatusBadgeProps = useCallback((status: string) => {
    switch (status) {
      case "published":
        return {
          backgroundColor: "#D1FAE5",
          textColor: "#065F46",
          text: "Published",
        };
      case "scheduled":
        return {
          backgroundColor: "#E0E7FF",
          textColor: "#3730A3",
          text: "Scheduled",
        };
      case "draft":
        return {
          backgroundColor: "#FEF3C7",
          textColor: "#92400E",
          text: "Draft",
        };
      default:
        return {
          backgroundColor: "#F3F4F6",
          textColor: "#374151",
          text: status,
        };
    }
  }, []);

  const getBorderColor = useCallback((status: string) => {
    switch (status) {
      case "published":
        return "#10B981";
      case "scheduled":
        return "#3E68FE";
      case "draft":
        return "#F59E0B";
      default:
        return "#E5E7EB";
    }
  }, []);

  const handleMenuPress = useCallback((event: any) => {
    const { pageX, pageY } = event.nativeEvent;
    setMenuPosition({ x: pageX, y: pageY });
    setMenuVisible(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuVisible(false);
  }, []);

  const statusBadge = useMemo(() => getStatusBadgeProps(announcement.status), []);
  const borderColor = useMemo(() => getBorderColor(announcement.status), []);

  const stripHtmlTags = useCallback((html: string) => {
    if (!html) return "";
    const withoutTags = html.replace(/<[^>]*>/g, "");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = withoutTags;
    return tempDiv.textContent || tempDiv.innerText || "";
  }, []);

  const threadContent = useMemo(
    () => (announcement.message ? stripHtmlTags(announcement.message) : ""),
    [announcement.message, stripHtmlTags]
  );

  return (
    <>
      <View style={[styles.container, { borderLeftColor: borderColor }]}>
        {/* Announcement Header */}
        <View style={styles.header}>
          <View style={styles.meta}>
            <CustomText text={announcement.title} weight="bold" style={styles.title} useTranslationText={false} />
            <View style={styles.details}>
              <CustomText text={announcement.author} size="s" style={styles.author} useTranslationText={false} />
              <CustomText text={announcement.date} size="xs" style={styles.date} useTranslationText={false} />
              <View>
                <Pressable onPress={() => setShowTargetDetails(!showTargetDetails)} style={styles.targetSummary}>
                  <CustomText
                    text="target_with_audience"
                    interpolation={{ targetAudience: announcement.targetAudience }}
                    size="xs"
                    style={styles.targetText}
                  />
                  {announcement.targetDetails && <CaretDown size={12} color="#667085" />}
                </Pressable>
                {showTargetDetails && announcement.targetDetails && (
                  <View style={styles.targetDetails}>
                    <CustomText
                      text={announcement.targetDetails}
                      size="xs"
                      style={styles.targetDetailsText}
                      useTranslationText={false}
                    />
                  </View>
                )}
              </View>
            </View>
          </View>
          <View style={styles.statusSection}>
            <Pill
              text={statusBadge.text}
              backgroundColor={statusBadge.backgroundColor}
              textColor={statusBadge.textColor}
              textSize="xs"
              borderWidth={0}
            />
            <Pressable style={styles.menuButton} onPress={handleMenuPress}>
              <DotsThree size={16} color="#667085" />
            </Pressable>
          </View>
        </View>

        {/* Announcement Content */}
        <View style={styles.content}>
          <CustomText
            text={
              announcement.message.length <= 150 || isExpanded ? threadContent : `${threadContent.slice(0, 120)}...`
            }
            weight="normal"
            style={styles.text}
            useTranslationText={false}
          />
          {announcement.message.length > 150 && (
            <Pressable style={styles.showMoreButton} onPress={() => setIsExpanded(!isExpanded)}>
              <CustomText text={isExpanded ? "Show Less" : "Show More"} size="s" style={styles.showMoreText} />
            </Pressable>
          )}
        </View>
      </View>

      {/* Action Menu */}
      <Menu
        visible={menuVisible}
        onDismiss={handleMenuClose}
        anchor={{ x: menuPosition.x, y: menuPosition.y }}
        contentStyle={styles.menu}>
        <Menu.Item
          onPress={() => {
            onEdit(announcement.id);
            handleMenuClose();
          }}
          title="Edit announcement"
          titleStyle={styles.menuItemText}
        />
        {announcement.status === "draft" && onPublish && (
          <Menu.Item
            onPress={() => {
              onPublish(announcement.id);
              handleMenuClose();
            }}
            title="Publish"
            titleStyle={styles.menuItemText}
          />
        )}
        {announcement.status === "scheduled" && onPublish && (
          <Menu.Item
            onPress={() => {
              onPublish(announcement.id);
              handleMenuClose();
            }}
            title="Publish now"
            titleStyle={styles.menuItemText}
          />
        )}
        {announcement.status === "scheduled" && onReschedule && (
          <Menu.Item
            onPress={() => {
              onReschedule(announcement.id);
              handleMenuClose();
            }}
            title="Reschedule"
            titleStyle={styles.menuItemText}
          />
        )}
        <Menu.Item
          onPress={() => {
            onDelete(announcement.id);
            handleMenuClose();
          }}
          title="Delete announcement"
          titleStyle={[styles.menuItemText, styles.deleteText]}
        />
      </Menu>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderLeftWidth: 4,
    marginBottom: 16,
  },
  header: {
    marginBottom: 16,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    zIndex: 10,
  },
  meta: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  title: {
    color: "#1E293B",
  },
  details: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  author: {
    color: "#374151",
    fontWeight: "500",
  },
  date: {
    color: "#64748B",
  },
  targetSummary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  targetText: {
    color: "#1E3DEF",
    backgroundColor: "#E0ECFF",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  targetDetails: {
    position: "absolute",
    top: 24,
    padding: 8,
    backgroundColor: "#F8FAFC",
    borderRadius: 4,
  },
  targetDetailsText: {
    color: "#374151",
  },
  statusSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    textTransform: "uppercase",
  },
  menuButton: {
    padding: 4,
  },
  content: {
    gap: 12,
  },
  text: {
    color: "#374151",
    lineHeight: 20,
  },
  showMoreButton: {
    alignSelf: "flex-start",
  },
  showMoreText: {
    color: "#3E68FE",
    textDecorationLine: "underline",
  },
  menu: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  menuItemText: {
    color: "#374151",
    fontSize: 14,
    fontWeight: "500",
  },
  deleteText: {
    color: "#DC2626",
  },
});

export default AnnouncementItem;
